import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles, addColorOpacity } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { MortgageCalculation } from '../../shared/types/mortgage';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

interface MortgageCalculationCardProps {
  calculation: MortgageCalculation;
  onView: () => void;
  onDelete: () => void;
  onCompare?: () => void;
  onViewSchedule?: () => void;
  isSelected?: boolean;
}

export function MortgageCalculationCard({
  calculation,
  onView,
  onDelete,
  onCompare,
  onViewSchedule,
  isSelected = false,
}: MortgageCalculationCardProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  const downPaymentDisplay = calculation.down_payment_type === 'percentage'
    ? `${calculation.down_payment}%`
    : `$${calculation.down_payment.toLocaleString()}`;

  return (
    <Card style={[styles.container, isSelected ? styles.containerSelected : undefined]} padding="sm">
      <View style={styles.header}>
        <View style={styles.info}>
          <Text style={styles.title}>
            {downPaymentDisplay} down • {calculation.interest_rate * 100}% • {calculation.loan_term_years}yr
          </Text>
          <Text style={styles.subtitle}>
            {new Date(calculation.created_at).toLocaleDateString()}
          </Text>
        </View>
        {onCompare && (
          <Pressable
            onPress={onCompare}
            style={[styles.checkboxContainer, isSelected ? styles.checkboxContainerSelected : null]}
          >
            <View style={[styles.checkbox, isSelected ? styles.checkboxSelected : null]}>
              {isSelected && <Text style={styles.checkmark}>✓</Text>}
            </View>
          </Pressable>
        )}
      </View>

      <View style={styles.metrics}>
        <View style={styles.metric}>
          <Text style={styles.metricLabel}>Monthly</Text>
          <Text style={[styles.metricValue, styles.metricValueHighlight]}>
            ${calculation.monthly_payment.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
        </View>
        <View style={styles.metric}>
          <Text style={styles.metricLabel}>Loan</Text>
          <Text style={styles.metricValue}>
            ${calculation.loan_amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
        </View>
        <View style={styles.metric}>
          <Text style={styles.metricLabel}>Interest</Text>
          <Text style={styles.metricValue}>
            ${calculation.total_interest.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
        </View>
      </View>

      <View style={styles.actions}>
        <Button title="View" onPress={onView} variant="primary" size="sm" style={styles.actionButton} />
        {onViewSchedule && (
          <Button title="Schedule" onPress={onViewSchedule} variant="outline" size="sm" style={styles.actionButton} />
        )}
        <Button title="Delete" onPress={onDelete} variant="danger" size="sm" style={styles.actionButton} />
      </View>
    </Card>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      flex: 1,
      minWidth: 280,
      maxWidth: 400,
    },
    containerSelected: {
      borderColor: tokens.color.primary,
      borderWidth: 2,
      backgroundColor: addColorOpacity(tokens.color.primaryContainer, 0.15),
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: utils.spacing.sm,
      paddingBottom: utils.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    info: {
      flex: 1,
    },
    title: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      marginBottom: 2,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: utils.typography.sizes.sm * utils.typography.lineHeight.tight,
    },
    subtitle: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.regular,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      marginTop: 2,
    },
    checkboxContainer: {
      padding: utils.spacing.xs,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.border, 0.1),
      ...Platform.select({
        web: {
          cursor: 'pointer' as any,
          transition: 'all 0.15s ease' as any,
        },
      }),
    },
    checkboxContainerSelected: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.15),
    },
    checkbox: {
      width: 22,
      height: 22,
      borderRadius: utils.radius.sm,
      borderWidth: 2,
      borderColor: tokens.color.primary,
      backgroundColor: tokens.color.surface,
      justifyContent: 'center',
      alignItems: 'center',
    },
    checkboxSelected: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    checkmark: {
      color: tokens.color.onPrimary,
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.bold,
    },
    metrics: {
      flexDirection: 'row',
      gap: utils.spacing.sm,
      marginBottom: utils.spacing.sm,
    },
    metric: {
      flex: 1,
      minWidth: 80,
    },
    metricLabel: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      marginBottom: 2,
      fontFamily: utils.typography.body.fontFamily,
      letterSpacing: 0.1,
      textTransform: 'uppercase',
    },
    metricValue: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: utils.typography.sizes.sm * utils.typography.lineHeight.tight,
    },
    metricValueHighlight: {
      color: tokens.color.primary,
    },
    actions: {
      flexDirection: 'row',
      gap: utils.spacing.xs,
      flexWrap: 'wrap',
    },
    actionButton: {
      flex: 1,
      minWidth: 70,
    },
  });
}

